export const SERVICE_WORKER_CACHE_CLEAR_TIMEOUT_MS = 3000;
export const SERVICE_WORKER_ACTIVATION_TIMEOUT_MS = 5000;

declare global {
    interface Window {
        __orcaWebReloadInProgress?: boolean;
    }
}

interface ControllerChangeWait {
    cancel: () => void;
    promise: Promise<void>;
}

const getServiceWorkerContainer = (): ServiceWorkerContainer | null => {
    if (!('serviceWorker' in navigator)) {
        return null;
    }

    return navigator.serviceWorker;
};

const waitForControllerChange = (serviceWorker: ServiceWorkerContainer): ControllerChangeWait => {
    let finish: () => void = () => undefined;

    const promise = new Promise<void>((resolve) => {
        const timeoutId = window.setTimeout(complete, SERVICE_WORKER_ACTIVATION_TIMEOUT_MS);

        function complete(): void {
            window.clearTimeout(timeoutId);
            serviceWorker.removeEventListener('controllerchange', complete);
            resolve();
        }

        finish = complete;
        serviceWorker.addEventListener('controllerchange', complete, { once: true });
    });

    return { promise, cancel: finish };
};

const waitForWaitingWorker = (registration: ServiceWorkerRegistration, installingWorker: ServiceWorker): Promise<ServiceWorker | null> => {
    if (registration.waiting) {
        return Promise.resolve(registration.waiting);
    }

    return new Promise((resolve) => {
        const timeoutId = window.setTimeout(() => complete(null), SERVICE_WORKER_ACTIVATION_TIMEOUT_MS);

        const handleStateChange = (): void => {
            if (registration.waiting) {
                complete(registration.waiting);
            } else if (installingWorker.state === 'activated' || installingWorker.state === 'redundant') {
                complete(null);
            }
        };

        const complete = (waitingWorker: ServiceWorker | null): void => {
            window.clearTimeout(timeoutId);
            installingWorker.removeEventListener('statechange', handleStateChange);
            resolve(waitingWorker);
        };

        installingWorker.addEventListener('statechange', handleStateChange);
        handleStateChange();
    });
};

/**
 * Sends the existing cache-clear request to the active worker and resolves when it
 * acknowledges the request or the worker cannot respond. This never touches IndexedDB.
 */
export const clearServiceWorkerCache = (): Promise<void> => {
    const serviceWorker = getServiceWorkerContainer();
    if (!serviceWorker) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        let completed = false;
        const timeoutId = window.setTimeout(() => {
            console.warn('[ServiceWorker] Cache clear acknowledgement timed out; continuing with the requested action.');
            complete();
        }, SERVICE_WORKER_CACHE_CLEAR_TIMEOUT_MS);

        const handleClearCacheCompleted = (event: MessageEvent): void => {
            if (event.data?.type === 'clear_cache_completed') {
                complete();
            }
        };

        function complete(): void {
            if (completed) {
                return;
            }
            completed = true;
            window.clearTimeout(timeoutId);
            serviceWorker.removeEventListener('message', handleClearCacheCompleted);
            resolve();
        }

        serviceWorker.addEventListener('message', handleClearCacheCompleted);

        serviceWorker
            .getRegistration()
            .then((registration) => {
                const activeWorker = registration?.active ?? serviceWorker.controller;
                if (!activeWorker) {
                    complete();
                    return;
                }

                try {
                    activeWorker.postMessage({ type: 'clear_cache' });
                } catch (error) {
                    console.warn('[ServiceWorker] Cache clear request failed; continuing with the requested action.', error);
                    complete();
                }
            })
            .catch((error) => {
                console.warn('[ServiceWorker] Could not resolve the active worker for cache clearing; continuing with the requested action.', error);
                complete();
            });
    });
};

/**
 * Performs a user-requested update check and, when a new worker is waiting, asks it
 * to activate. Controller takeover is awaited only for a discovered new worker and
 * is time-bounded so a failed update cannot block the user's reload.
 */
export const updateAndActivateServiceWorker = async (): Promise<void> => {
    const serviceWorker = getServiceWorkerContainer();
    if (!serviceWorker) {
        return;
    }

    let registration: ServiceWorkerRegistration | undefined;
    try {
        registration = await serviceWorker.getRegistration();
    } catch {
        return;
    }

    if (!registration) {
        return;
    }

    let installingWorker = registration.installing;
    const handleUpdateFound = (): void => {
        installingWorker = registration?.installing ?? installingWorker;
    };

    registration.addEventListener('updatefound', handleUpdateFound);

    try {
        await registration.update();
    } catch {
        // A previously downloaded worker may already be waiting and can still activate.
    } finally {
        registration.removeEventListener('updatefound', handleUpdateFound);
    }

    const installingWorkerAfterUpdate = registration.installing ?? installingWorker;
    const waitingWorker =
        registration.waiting ?? (installingWorkerAfterUpdate ? await waitForWaitingWorker(registration, installingWorkerAfterUpdate) : null);
    if (!waitingWorker) {
        return;
    }

    const controllerChange = waitForControllerChange(serviceWorker);

    try {
        waitingWorker.postMessage('SKIP_WAITING');
    } catch {
        controllerChange.cancel();
        return;
    }

    await controllerChange.promise;
};

export const reloadPageOnce = (): void => {
    if (window.__orcaWebReloadInProgress) {
        return;
    }

    window.__orcaWebReloadInProgress = true;
    window.location.reload();
};
